import { useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowRight, Check, CheckCircle, Loader2 } from 'lucide-react'
import CheckoutLayout from '@/frontend/layouts/CheckoutLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { WorkerSubscription } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  worker?: {
    id: string
    name: string
    billing: WorkerSubscription
  } | null
  checkout?: {
    id: string
    payment_status: string | null
    amount_total: number | null
    currency: string | null
  } | null
  subscription_active: boolean
}

function formatCurrency(amount: number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined || !currency) return null

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100)
}

export default function CheckoutSuccess({ worker, checkout, subscription_active }: Props) {
  const continueHref = worker
    ? `/dashboard/digital_workers/new?worker_id=${worker.id}&onboarding_step=${subscription_active ? 'responsible' : 'subscription'}`
    : '/dashboard'
  const formattedAmount = formatCurrency(checkout?.amount_total, checkout?.currency)

  useEffect(() => {
    if (subscription_active) return

    const timer = setTimeout(() => router.reload({ only: ['worker', 'subscription_active'] }), 2500)
    return () => clearTimeout(timer)
  }, [subscription_active])

  return (
    <CheckoutLayout>
      <div className="min-h-screen flex items-center justify-center px-4 py-28">
        <div className="w-full max-w-xl text-center">
          <div className="mb-6 flex justify-center">
            {subscription_active ? (
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-emerald-500/20 blur-xl" aria-hidden />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30">
                  <CheckCircle className="h-11 w-11 text-emerald-400" />
                </div>
              </div>
            ) : (
              <Loader2 className="h-16 w-16 animate-spin text-sky-300" />
            )}
          </div>

          <h1 className="text-3xl font-bold text-white">
            {subscription_active ? 'Assinatura ativada' : 'Pagamento recebido'}
          </h1>
          <p className="mt-3 text-base text-slate-300">
            {worker
              ? `${worker.name} ${subscription_active ? 'já pode continuar a configuração.' : 'fica disponível assim que o Stripe confirmar a assinatura.'}`
              : 'Estamos atualizando a assinatura deste colaborador.'}
          </p>

          {subscription_active && (
            <ul className="mt-6 inline-flex flex-col gap-2 text-left">
              {[
                'Pagamento confirmado',
                'Assinatura ativa',
                `${worker?.name ?? 'O colaborador'} disponível para a próxima etapa`,
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                  {item}
                </li>
              ))}
            </ul>
          )}

          <Card className="mt-8 border-slate-700 bg-slate-900/70 text-left">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-slate-400">Status</span>
                {subscription_active ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-sm font-medium text-emerald-300 ring-1 ring-emerald-400/30">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />
                    Ativa
                  </span>
                ) : (
                  <span className="text-sm font-medium text-slate-300">Processando</span>
                )}
              </div>
              {worker?.billing.plan_name && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-400">Plano</span>
                  <span className="text-sm font-medium text-white">{worker.billing.plan_name}</span>
                </div>
              )}
              {formattedAmount && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-400">Valor</span>
                  <span className="text-sm font-medium text-white">{formattedAmount}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Button
            asChild
            size="lg"
            className="mt-8 bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-violet-900/40 transition-all hover:from-indigo-400 hover:to-violet-400"
          >
            <Link href={continueHref}>
              Continuar configuração
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </CheckoutLayout>
  )
}
